/**
 * The hero's top nav — logo, links, More menu, theme toggle and the booking
 * call to action.
 *
 * Extracted from ElfHero unchanged so every hero variant carries the same
 * header; the markup and class names are the original ones, so `.elf-hero__nav`
 * in elf-landing.css still styles it.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import ElfModeToggle from './ElfModeToggle';
import ElfNavMenu from './ElfNavMenu';
import useMagnetic from './useMagnetic';
import { useConsultOpener } from './elfConsult';
import { useAuth } from '../../../auth';
import { isAdmin, route } from '../../../../shared/utils';
import {
  ELF_NAV,
  ELF_NAV_MORE,
  ELF_NAV_MORE_COMPACT,
  ELF_NAV_PRIMARY,
  navLinkClass,
  type NavItem,
} from './ElfPageNav';
import elfLogo from '../../../../assets/elf/elfcircle-logo.png';

export const ElfHeroNav: React.FC<{
  /** Path of the page being rendered, so its link shows the `here` state. */
  current?: string;
}> = ({ current }) => {
  const magnetic = useMagnetic();
  const { user, token } = useAuth();
  const showEnquiries = Boolean(token) && isAdmin(user);
  // Same entries the inline row renders, for the narrow-viewport menu.
  const menuItems: NavItem[] = showEnquiries
    ? [...ELF_NAV, { label: 'Enquiries', to: '/enquiries' }]
    : ELF_NAV;
  // Same split as the inner-page header — the hero row is tighter still, since
  // its call to action reads "Book a Consultation" rather than "Book a call".
  const moreItems: NavItem[] = showEnquiries
    ? [...ELF_NAV_MORE, { label: 'Enquiries', to: '/enquiries' }]
    : ELF_NAV_MORE;
  const compactMoreItems: NavItem[] = showEnquiries
    ? [...ELF_NAV_MORE_COMPACT, { label: 'Enquiries', to: '/enquiries' }]
    : ELF_NAV_MORE_COMPACT;
  const bookConsult = useConsultOpener({ source: 'hero', title: 'Book a consultation' });

  return (
    <div className="elf-hero__nav">
      <img
        className="plate anL elf-hero__logo"
        src={elfLogo}
        alt="ELF-CIRCLE"
        style={{ animationDelay: '.1s' }}
      />
      <div className="elf-hero__navlinks">
        {ELF_NAV_PRIMARY.map((item, i) => (
          <Link
            key={item.to}
            to={route(item.to)}
            className={navLinkClass(item, current, 'an')}
            aria-current={item.to === current ? 'page' : undefined}
            style={{ animationDelay: `${0.2 + i * 0.06}s` }}
          >
            {item.label}
          </Link>
        ))}
        <span className="an elf-hero__more elf-navmore--wide" style={{ animationDelay: '.62s' }}>
          <ElfNavMenu variant="more" items={moreItems} />
        </span>
        <span className="an elf-hero__more elf-navmore--compact" style={{ animationDelay: '.62s' }}>
          <ElfNavMenu variant="more" items={compactMoreItems} />
        </span>
        <span className="an" style={{ animationDelay: '.44s' }}>
          <ElfModeToggle />
        </span>
        <button
          type="button"
          className="cta mag an elf-hero__navcta"
          style={{ animationDelay: '.5s' }}
          {...magnetic}
          onClick={bookConsult}
        >
          Book a Consultation
        </button>
        <ElfNavMenu items={menuItems} />
      </div>
    </div>
  );
};

export default ElfHeroNav;
